import React, { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CheckCircle, Cancel, Description, AttachMoney, SettingsEthernet, Map, Schedule } from '@material-ui/icons';
import * as Sentry from '@sentry/react';
import { UserContext } from '@/contexts/UserContext';
import { SectionSubtitle } from '@/styles/blocks';
import { greens, greys, oranges, reds } from '@/styles/colors';
import { colorTints } from './DocStatusIcon';
import DocumentUploadModal from './DocumentUploadModal';
import IntegrationsModal from './IntegrationsModal';
import PricingModal from './PricingModal';
import { ShipperBlock, CopyableId } from './blocks';
import { CorpAdminContext, MARKETPLACE_KEY } from '../store';
import { GET_CORP_INTEGRATION_OVERRIDES, INSERT_CORP_INTEGRATION_OVERRIDE } from '../graphql';

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(${(p) => p.columns || 4}, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 1rem;
`;

const StatusCard = styled.button`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.85rem 1rem;
    border-radius: 6px;
    border: 1px solid ${(p) => p.$borderColor};
    background: ${(p) => p.$bgColor};
    color: ${(p) => p.$textColor};
    text-align: left;
    cursor: ${(p) => (p.onClick && !p.disabled ? 'pointer' : 'default')};
    font: inherit;
    transition: background-color 120ms ease, border-color 120ms ease;
    min-width: 0;

    &:hover {
        background: ${(p) => (p.onClick && !p.disabled ? p.$hoverBgColor : p.$bgColor)};
        border-color: ${(p) => (p.onClick && !p.disabled ? p.$textColor : p.$borderColor)};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.65;
    }
`;

const CardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 14px;
    font-weight: 600;
`;

const CardInfo = styled.div`
    font-size: 13px;
    line-height: 1.3;
    color: ${greys[1]};
    flex: 1;
    min-height: 1.3em;
`;

const CardStatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const StatusCardView = ({
    titleIcon: TitleIcon,
    title,
    info,
    statusLabel,
    statusIcon: StatusIcon,
    color,
    onClick,
    disabled,
}) => {
    const tints = colorTints(color);
    return (
        <StatusCard type="button" onClick={onClick} disabled={disabled} {...tints}>
            <CardTitle>
                <TitleIcon
                    css={css`
                        font-size: 18px;
                        color: ${color};
                    `}
                />
                {title}
            </CardTitle>
            <CardInfo>{info}</CardInfo>
            <CardStatusRow>
                <StatusIcon
                    css={css`
                        font-size: 14px;
                        color: ${color};
                    `}
                />
                {statusLabel}
            </CardStatusRow>
        </StatusCard>
    );
};

const buildIntegrationCard = (integrations) => {
    // MANUAL rows are bootstrap placeholders that the integrations modal renders in setup
    // mode — don't count them for the card's "configured" tally.
    const configured = integrations.filter((i) => i.type !== 'MANUAL');
    const count = configured.length;
    if (count === 0) {
        return { color: reds.primary, info: 'None configured', statusIcon: Cancel, statusLabel: 'None' };
    }
    const statuses = configured.map((i) => i.status);
    if (statuses.some((s) => !s || s === 'ERROR')) {
        return { color: reds.primary, info: `${count} configured`, statusIcon: Cancel, statusLabel: 'Error' };
    }
    if (statuses.some((s) => s === 'PENDING')) {
        return { color: oranges.primary, info: `${count} configured`, statusIcon: Schedule, statusLabel: 'Pending' };
    }
    // All COMPLETE.
    return { color: greens.primary, info: `${count} configured`, statusIcon: CheckCircle, statusLabel: 'Active' };
};

const buildPricingCard = (overrides) => {
    const count = overrides.length;
    const ok = count > 0 && overrides.every((po) => po.enabled);
    return {
        color: ok ? greens.primary : reds.primary,
        info: count > 0 ? `${count} Rate${count === 1 ? '' : 's'}` : 'None configured',
        statusIcon: ok ? CheckCircle : Cancel,
        statusLabel: ok ? 'Active' : count > 0 ? 'Inactive' : 'None',
    };
};

const buildDeliveryProfileCard = ({ assignment, isMarketplaceSection, carrier_id }) => {
    if (!assignment?.profileName) {
        return { color: greys[2], info: 'No profile applied', statusIcon: Cancel, statusLabel: 'Pending' };
    }
    if (isMarketplaceSection || assignment.mappedCarrierIds.has(carrier_id)) {
        return { color: greens.primary, info: assignment.profileName, statusIcon: CheckCircle, statusLabel: 'Active' };
    }
    return { color: reds.primary, info: assignment.profileName, statusIcon: Cancel, statusLabel: 'Inactive' };
};

const buildRateAddendumCard = (rateAddendum, applicable) => {
    if (!applicable) {
        return {
            color: greys[2],
            info: 'Not applicable',
            statusIcon: Cancel,
            statusLabel: 'N/A',
        };
    }
    if (rateAddendum) {
        return {
            color: greens.primary,
            info: rateAddendum.name || 'On file',
            statusIcon: CheckCircle,
            statusLabel: 'Complete',
        };
    }
    return {
        color: reds.primary,
        info: 'No document on file',
        statusIcon: Cancel,
        statusLabel: 'Upload',
    };
};

const ShipperSection = ({ carrier_id, carrierLegs, shipper_id, data, assetType }) => {
    const { admin_clients } = useContext(UserContext);
    const { state, callbacks } = useContext(CorpAdminContext);
    const { rateAddendumsByCarrierShipper, assignmentsByShipper } = state;
    const { setNotification } = callbacks;
    const [openModal, setOpenModal] = useState(null); // 'integrations' | 'pricing' | 'profile' | 'rates' | null
    const shipper = (admin_clients || []).find((c) => c.client_id === shipper_id);
    const { integrations, carrierPricing, shipperPricing } = data;

    const isMarketplace = carrier_id === MARKETPLACE_KEY;
    const rateAddendumApplicable = assetType === 'light' && !isMarketplace;
    const rateAddendum = rateAddendumApplicable
        ? rateAddendumsByCarrierShipper?.[carrier_id]?.[shipper_id] || null
        : null;
    const shipperName = shipper?.business_name || 'Unknown Shipper';

    console.log(assignmentsByShipper);

    const integrationCard = buildIntegrationCard(integrations);
    const pricingCard = buildPricingCard([...shipperPricing, ...carrierPricing]);
    const deliveryProfileCard = buildDeliveryProfileCard({
        assignment: assignmentsByShipper?.[shipper_id],
        isMarketplaceSection: isMarketplace,
        carrier_id,
    });
    const rateAddendumCard = buildRateAddendumCard(rateAddendum, rateAddendumApplicable);

    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id),
        [admin_clients]
    );

    const [insertOverride] = useMutation(INSERT_CORP_INTEGRATION_OVERRIDE, {
        update(cache, { data: result }) {
            const newOverride = result?.insert_integration_overrides_one;
            if (!newOverride) return;
            try {
                const existing = cache.readQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (!existing) return;
                cache.writeQuery({
                    query: GET_CORP_INTEGRATION_OVERRIDES,
                    variables: { orgShipperIds },
                    data: {
                        integration_overrides: [...existing.integration_overrides, newOverride],
                    },
                });
            } catch {
                // cache miss is non-fatal
            }
        },
    });

    const handleIntegrationsClick = async () => {
        // Section exists from a pricing relationship but no integration_override yet —
        // bootstrap a MANUAL row so the modal has something to render in setup mode.
        if (integrations.length === 0) {
            const isMarketplace = carrier_id === MARKETPLACE_KEY;
            try {
                await insertOverride({
                    variables: {
                        object: {
                            carrier_id: isMarketplace ? null : carrier_id,
                            shipper_id,
                            client_id: isMarketplace ? shipper_id : carrier_id,
                            type: 'MANUAL',
                        },
                    },
                });
            } catch (err) {
                Sentry.captureException(err);
                setNotification?.({ severity: 'error', message: 'Failed to start integration setup' });
                return;
            }
        }
        setOpenModal('integrations');
    };

    return (
        <ShipperBlock>
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    min-width: 0;
                `}
            >
                <SectionSubtitle
                    css={css`
                        margin-bottom: 0;
                        min-width: 200px;
                        flex-shrink: 0;
                    `}
                >
                    {shipperName}
                </SectionSubtitle>
                <CopyableId label="Shipper ID" value={shipper_id} />
            </div>

            <CardGrid columns={isMarketplace ? 2 : 4}>
                {!isMarketplace && (
                    <StatusCardView
                        titleIcon={SettingsEthernet}
                        title="Integrations"
                        onClick={handleIntegrationsClick}
                        {...integrationCard}
                    />
                )}
                <StatusCardView
                    titleIcon={AttachMoney}
                    title="Pricing"
                    onClick={() => setOpenModal('pricing')}
                    {...pricingCard}
                />
                <StatusCardView titleIcon={Map} title="Delivery Profile" {...deliveryProfileCard} disabled />
                {!isMarketplace && (
                    <StatusCardView
                        titleIcon={Description}
                        title="Rate Addendum"
                        onClick={rateAddendumApplicable ? () => setOpenModal('rates') : undefined}
                        disabled={!rateAddendumApplicable}
                        {...rateAddendumCard}
                    />
                )}
            </CardGrid>

            <IntegrationsModal
                open={openModal === 'integrations'}
                onClose={() => setOpenModal(null)}
                integrations={integrations}
                shipperName={shipperName}
            />
            <PricingModal
                open={openModal === 'pricing'}
                onClose={() => setOpenModal(null)}
                carrier_id={carrier_id}
                carrierLegs={carrierLegs}
                carrierPricing={carrierPricing}
                shipperPricing={shipperPricing}
                shipper_id={shipper_id}
                shipperName={shipperName}
            />
            {rateAddendumApplicable && (
                <DocumentUploadModal
                    open={openModal === 'rates'}
                    onClose={() => setOpenModal(null)}
                    type="RATES"
                    client_id={carrier_id}
                    partner_client_id={shipper_id}
                    existingDoc={rateAddendum}
                    contextLabel={shipperName}
                />
            )}
        </ShipperBlock>
    );
};

export default ShipperSection;
